import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TextInput from '../../widgets/TextInput';
import NumberInput from '../../widgets/NumberInput';
import SelectInput from '../../widgets/SelectInput';
import { CustomerInvoice, CustomerInvoiceLineItem } from '../../../models/CustomerInvoice';
import { Customer } from '../../../models/Customer';
import { FiscalYear } from '../../../models/FiscalYear';
import { VatRate } from '../../../models/VatRate';
import { ProductOrService } from '../../../models/ProductOrService';
import { Project, ProjectsRestDataProvider } from '../../../data/ProjectsRestDataProvider';
import { TimeWritingRestDataProvider } from '../../../data/TimeWritingRestDataProvider';
import { FaSave, FaTimes, FaTrash, FaPlus, FaEye, FaBook, FaMoneyBillWave, FaFileImport } from 'react-icons/fa';
import AttachmentsPanel from '../../common/Attachments/AttachmentsPanel';
import InvoiceView from './InvoiceView';
import PopupDialog from '../../common/PopupDialog/PopupDialog';
import GenericVerticalTabs, { TabConfig } from '../../common/GenericVerticalTabs/GenericVerticalTabs';
import ModalDialog from '../../common/ModalDialog/ModalDialog';
import './CustomerInvoiceDialog.scss';

interface CustomerInvoiceDialogProps {
    open: boolean;
    invoice: CustomerInvoice | null;
    customers: Customer[];
    fiscalYears: FiscalYear[];
    vatRates: VatRate[];
    products: ProductOrService[];
    onSave: (invoice: CustomerInvoice) => Promise<CustomerInvoice>;
    onDelete: (invoice: CustomerInvoice) => void;
    onBook: (invoice: CustomerInvoice) => Promise<void>;
    onBookPayment: (invoice: CustomerInvoice, paymentDate: string) => Promise<void>;
    onClose: () => void;
}

const defaultVatRate = (vatRates: VatRate[]): VatRate =>
    vatRates.find(v => v.isActive) ??
    vatRates[0] ??
    { id: '', name: '', percentage: 0, isMoved: false, isExempt: false, isActive: true };

const newLineItem = (invoiceId: string, vatRates: VatRate[]): CustomerInvoiceLineItem => ({
    id: crypto.randomUUID(),
    customerInvoiceId: invoiceId,
    description: '',
    quantity: 1,
    unitPriceExVat: 0,
    vatRate: defaultVatRate(vatRates),
    totalPriceExVat: 0,
    totalPriceIncVat: 0,
});

const computeItem = (item: CustomerInvoiceLineItem): CustomerInvoiceLineItem => {
    const totalPriceExVat = item.quantity * item.unitPriceExVat;
    const effectiveVatPct = (item.vatRate?.isMoved || item.vatRate?.isExempt) ? 0 : (item.vatRate?.percentage ?? 0);
    const totalPriceIncVat = totalPriceExVat * (1 + effectiveVatPct / 100);
    return { ...item, totalPriceExVat, totalPriceIncVat };
};

const emptyInvoice: CustomerInvoice = {
    id: '',
    customerId: '',
    fiscalYearId: null,
    invoiceDate: null,
    paymentDate: null,
    deliveryDate: null,
    submitDate: null,
    isPaid: false,
    paymentTermInDays: 30,
    reference: '',
    invoiceNumber: '',
    customerReference: '',
    comments: '',
    invoiceFileAttachmentId: null,
    invoiceEmailAttachmentId: null,
    paymentDueDate: null,
    lineItems: [],
    totalAmountExVat: 0,
    totalAmountIncVat: 0,
    isBooked: false,
    bookedAt: null,
    includeTimeWritingOverview: false,
    projectId: null,
    timeWritingFrom: null,
    timeWritingTo: null,
};

const CustomerInvoiceDialog: React.FC<CustomerInvoiceDialogProps> = ({
    open, invoice, customers, fiscalYears, vatRates, products, onSave, onDelete, onBook, onBookPayment, onClose,
}) => {
    const { t } = useTranslation(['common', 'screens', 'validation']);
    const [data, setData] = useState<CustomerInvoice>(invoice ?? emptyInvoice);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showBookConfirm, setShowBookConfirm] = useState(false);
    const [showBookPaymentConfirm, setShowBookPaymentConfirm] = useState(false);
    const [bookPaymentDate, setBookPaymentDate] = useState('');
    const [showInvoiceView, setShowInvoiceView] = useState(false);
    const [lineItemProductIds, setLineItemProductIds] = useState<Record<string, string>>({});
    const [activeTab, setActiveTab] = useState('general');
    const [showImportTimewriting, setShowImportTimewriting] = useState(false);

    useEffect(() => {
        setData(invoice ?? { ...emptyInvoice });
        setValidationError(null);
        setShowDeleteConfirm(false);
        setShowBookConfirm(false);
        setShowBookPaymentConfirm(false);
        setBookPaymentDate('');
        setLineItemProductIds({});
        setActiveTab('general');
        setShowImportTimewriting(false);
    }, [invoice, open]);

    if (!open) return null;

    const isNew = !invoice?.id;
    const set = <K extends keyof CustomerInvoice>(field: K, value: CustomerInvoice[K]) =>
        setData(prev => ({ ...prev, [field]: value }));

    const addLineItem = () => {
        setData(prev => ({
            ...prev,
            lineItems: [...prev.lineItems, newLineItem(prev.id, vatRates)],
        }));
    };

    const removeLineItem = (itemId: string) => {
        setData(prev => ({ ...prev, lineItems: prev.lineItems.filter(i => i.id !== itemId) }));
        setLineItemProductIds(prev => {
            const next = { ...prev };
            delete next[itemId];
            return next;
        });
    };

    const updateLineItem = (itemId: string, updates: Partial<CustomerInvoiceLineItem>) => {
        setData(prev => ({
            ...prev,
            lineItems: prev.lineItems.map(item =>
                item.id === itemId ? computeItem({ ...item, ...updates }) : item
            ),
        }));
    };

    const handleProductSelect = (itemId: string, productId: string) => {
        setLineItemProductIds(prev => ({ ...prev, [itemId]: productId }));
        if (!productId) return;
        const product = products.find(p => p.id === productId);
        if (!product) return;
        const vatRate = product.vat
            ? (vatRates.find(v => v.id === product.vat!.id) ?? defaultVatRate(vatRates))
            : defaultVatRate(vatRates);
        updateLineItem(itemId, {
            description: product.name,
            unitPriceExVat: product.unitPriceExVat ?? 0,
            vatRate,
        });
    };

    const handleImportTimewriting = (item: CustomerInvoiceLineItem, projectId: string, from: string, to: string) => {
        setData(prev => ({
            ...prev,
            lineItems: [...prev.lineItems, item],
            projectId,
            timeWritingFrom: from,
            timeWritingTo: to,
        }));
        setShowImportTimewriting(false);
    };

    const totalExVat = data.lineItems.reduce((sum, i) => sum + i.totalPriceExVat, 0);
    const totalIncVat = data.lineItems.reduce((sum, i) => sum + i.totalPriceIncVat, 0);

    const sanitizeInvoice = (inv: CustomerInvoice): CustomerInvoice => ({
        ...inv,
        fiscalYearId: inv.fiscalYearId || null,
        invoiceDate: inv.invoiceDate || null,
        paymentDate: inv.paymentDate || null,
        deliveryDate: inv.deliveryDate || null,
        submitDate: inv.submitDate || null,
        invoiceFileAttachmentId: inv.invoiceFileAttachmentId || null,
        invoiceEmailAttachmentId: inv.invoiceEmailAttachmentId || null,
    });

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setValidationError(null);
        if (!data.customerId || !data.fiscalYearId) {
            setValidationError(t('validation:requiredFields'));
            setActiveTab('general');
            return;
        }
        try {
            const saved = await onSave(sanitizeInvoice({ ...data, totalAmountExVat: totalExVat, totalAmountIncVat: totalIncVat }));
            setData(saved);
        } catch {
            // parent handles error display
        }
    };

    const customerOptions = customers.map(c => ({ label: c.name, value: c.id }));
    const fiscalYearOptions = fiscalYears.map(f => ({ label: f.name, value: f.id }));
    const productOptions = [
        { label: t('screens:customerInvoiceDialog.manualEntry'), value: '' },
        ...products.map(p => ({ label: p.articleCode ? `[${p.articleCode}] ${p.name}` : p.name, value: p.id })),
    ];

    const tabs: TabConfig[] = [
        {
            key: 'general',
            label: t('screens:customerInvoiceDialog.tabs.generalDetails'),
            content: (
                <div className="dialog-section">
                    {validationError && (
                        <div className="alert alert-danger">{validationError}</div>
                    )}
                    <div className="dialog-row">
                        <div className="dialog-col dialog-col--wide">
                            <SelectInput
                                label={t('screens:customerInvoiceDialog.fields.customer')}
                                value={data.customerId}
                                options={customerOptions}
                                required
                                onChange={v => set('customerId', v)}
                            />
                        </div>
                        <div className="dialog-col">
                            <SelectInput
                                label={t('screens:customerInvoiceDialog.fields.fiscalYear')}
                                value={data.fiscalYearId ?? ''}
                                options={fiscalYearOptions}
                                required
                                onChange={v => set('fiscalYearId', v)}
                            />
                        </div>
                        <div className="dialog-col dialog-col--narrow">
                            <NumberInput
                                label={t('screens:customerInvoiceDialog.fields.paymentTerm')}
                                value={data.paymentTermInDays}
                                min={0}
                                onChange={v => set('paymentTermInDays', v)}
                            />
                        </div>
                    </div>
                    <div className="dialog-row">
                        <div className="dialog-col">
                            <div className="mb-3">
                                <label className="form-label">{t('screens:customerInvoiceDialog.fields.invoiceDate')}</label>
                                <input type="date" className="form-control" value={data.invoiceDate ?? ''} onChange={e => set('invoiceDate', e.target.value)} />
                            </div>
                        </div>
                        <div className="dialog-col">
                            <div className="mb-3">
                                <label className="form-label">{t('screens:customerInvoiceDialog.fields.deliveryDate')}</label>
                                <input type="date" className="form-control" value={data.deliveryDate ?? ''} onChange={e => set('deliveryDate', e.target.value)} />
                            </div>
                        </div>
                        <div className="dialog-col">
                            <div className="mb-3">
                                <label className="form-label">{t('screens:customerInvoiceDialog.fields.submitDate')}</label>
                                <input type="date" className="form-control" value={data.submitDate ?? ''} onChange={e => set('submitDate', e.target.value)} />
                            </div>
                        </div>
                        <div className="dialog-col">
                            <div className="mb-3">
                                <label className="form-label">{t('screens:customerInvoiceDialog.fields.paymentDate')}</label>
                                <input type="date" className="form-control" value={data.paymentDate ?? ''} onChange={e => set('paymentDate', e.target.value)} />
                            </div>
                        </div>
                    </div>
                    <div className="dialog-row">
                        <div className="dialog-col">
                            <TextInput label={t('screens:customerInvoiceDialog.fields.invoiceNumber')} value={data.invoiceNumber ?? ''} onChange={v => set('invoiceNumber', v)} />
                        </div>
                        <div className="dialog-col">
                            <TextInput label={t('screens:customerInvoiceDialog.fields.reference')} value={data.reference ?? ''} onChange={v => set('reference', v)} />
                        </div>
                        <div className="dialog-col">
                            <TextInput label={t('screens:customerInvoiceDialog.fields.customerReference')} value={data.customerReference ?? ''} onChange={v => set('customerReference', v)} />
                        </div>
                        <div className="dialog-col">
                            <SelectInput
                                label={t('screens:customerInvoiceDialog.fields.paymentStatus')}
                                value={data.isPaid ? 'true' : 'false'}
                                options={[
                                    { label: t('common:unpaid'), value: 'false' },
                                    { label: t('common:paid'), value: 'true' },
                                ]}
                                onChange={v => set('isPaid', v === 'true')}
                            />
                        </div>
                    </div>
                    <div className="dialog-row">
                        <div className="dialog-col dialog-col--full">
                            <div className="mb-3">
                                <label className="form-label">{t('screens:customerInvoiceDialog.fields.comments')}</label>
                                <textarea className="form-control" rows={12} value={data.comments ?? ''} onChange={e => set('comments', e.target.value)} />
                            </div>
                        </div>
                    </div>
                </div>
            ),
        },
        {
            key: 'lineitems',
            label: t('screens:customerInvoiceDialog.tabs.lineItems'),
            content: (
                <>
                    <div className="dialog-section">
                        <div className="inv-dialog__li-add">
                            <button type="button" className="btn btn-outline-primary btn-sm" onClick={addLineItem}>
                                <FaPlus style={{ marginRight: 5 }} />{t('screens:customerInvoiceDialog.lineItems.addLineItem')}
                            </button>
                            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowImportTimewriting(true)}>
                                <FaFileImport style={{ marginRight: 5 }} />{t('screens:customerInvoiceDialog.lineItems.importTimewriting')}
                            </button>
                        </div>
                        <div className="inv-dialog__include-tw">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="include-tw-overview"
                                    checked={data.includeTimeWritingOverview ?? false}
                                    onChange={e => set('includeTimeWritingOverview', e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="include-tw-overview">
                                    {t('screens:customerInvoiceDialog.lineItems.includeOverview')}
                                </label>
                            </div>
                        </div>
                        <div className="dialog-section-title">{t('screens:customerInvoiceDialog.lineItems.title')}</div>
                        <div className="inv-dialog__line-items">
                            {data.lineItems.length > 0 && (
                                <div className="inv-dialog__li-header">
                                    <span className="inv-dialog__li-cell inv-dialog__li-cell--product">{t('screens:customerInvoiceDialog.lineItems.columns.product')}</span>
                                    <span className="inv-dialog__li-cell inv-dialog__li-cell--desc">{t('screens:customerInvoiceDialog.lineItems.columns.description')}</span>
                                    <span className="inv-dialog__li-cell inv-dialog__li-cell--qty">{t('screens:customerInvoiceDialog.lineItems.columns.qty')}</span>
                                    <span className="inv-dialog__li-cell inv-dialog__li-cell--price">{t('screens:customerInvoiceDialog.lineItems.columns.unitPrice')}</span>
                                    <span className="inv-dialog__li-cell inv-dialog__li-cell--vat">{t('screens:customerInvoiceDialog.lineItems.columns.vat')}</span>
                                    <span className="inv-dialog__li-cell inv-dialog__li-cell--total">{t('screens:customerInvoiceDialog.lineItems.columns.totalExVat')}</span>
                                    <span className="inv-dialog__li-cell inv-dialog__li-cell--total">{t('screens:customerInvoiceDialog.lineItems.columns.totalIncVat')}</span>
                                    <span className="inv-dialog__li-cell inv-dialog__li-cell--action"></span>
                                </div>
                            )}
                            <div className="inv-dialog__li-rows">
                                {data.lineItems.map(item => (
                                    <div key={item.id} className="inv-dialog__li-row">
                                        <div className="inv-dialog__li-cell inv-dialog__li-cell--product">
                                            <SelectInput
                                                label=""
                                                small
                                                value={lineItemProductIds[item.id] ?? ''}
                                                options={productOptions}
                                                onChange={v => handleProductSelect(item.id, v)}
                                            />
                                        </div>
                                        <div className="inv-dialog__li-cell inv-dialog__li-cell--desc">
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                value={item.description}
                                                placeholder={t('screens:customerInvoiceDialog.lineItems.columns.description')}
                                                onChange={e => updateLineItem(item.id, { description: e.target.value })}
                                            />
                                        </div>
                                        <div className="inv-dialog__li-cell inv-dialog__li-cell--qty">
                                            <input
                                                type="number"
                                                className="form-control form-control-sm"
                                                value={item.quantity}
                                                min={0}
                                                step="0.01"
                                                onChange={e => updateLineItem(item.id, { quantity: Number(e.target.value) })}
                                            />
                                        </div>
                                        <div className="inv-dialog__li-cell inv-dialog__li-cell--price">
                                            <input
                                                type="number"
                                                className="form-control form-control-sm"
                                                value={item.unitPriceExVat}
                                                min={0}
                                                step="0.01"
                                                onChange={e => updateLineItem(item.id, { unitPriceExVat: Number(e.target.value) })}
                                            />
                                        </div>
                                        <div className="inv-dialog__li-cell inv-dialog__li-cell--vat">
                                            <SelectInput
                                                label=""
                                                small
                                                value={item.vatRate?.id ?? ''}
                                                options={vatRates.map(v => ({ label: `${v.name} (${v.percentage}%)`, value: v.id }))}
                                                onChange={v => {
                                                    const vr = vatRates.find(r => r.id === v) ?? defaultVatRate(vatRates);
                                                    updateLineItem(item.id, { vatRate: vr });
                                                }}
                                            />
                                        </div>
                                        <div className="inv-dialog__li-cell inv-dialog__li-cell--total">
                                            <span className="inv-dialog__li-readonly">€ {item.totalPriceExVat.toFixed(2)}</span>
                                        </div>
                                        <div className="inv-dialog__li-cell inv-dialog__li-cell--total">
                                            <span className="inv-dialog__li-readonly">€ {item.totalPriceIncVat.toFixed(2)}</span>
                                        </div>
                                        <div className="inv-dialog__li-cell inv-dialog__li-cell--action">
                                            <button
                                                type="button"
                                                className="btn btn-outline-danger btn-sm"
                                                onClick={() => removeLineItem(item.id)}
                                                title="Remove line item"
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="dialog-section">
                        <div className="dialog-section-title">{t('screens:customerInvoiceDialog.totals.title')}</div>
                        <div className="inv-dialog__totals">
                            <div className="inv-dialog__total-row">
                                <span className="inv-dialog__total-label">{t('screens:customerInvoiceDialog.totals.totalExVat')}</span>
                                <span className="inv-dialog__total-value">€ {totalExVat.toFixed(2)}</span>
                            </div>
                            <div className="inv-dialog__total-row inv-dialog__total-row--grand">
                                <span className="inv-dialog__total-label">{t('screens:customerInvoiceDialog.totals.totalIncVat')}</span>
                                <span className="inv-dialog__total-value">€ {totalIncVat.toFixed(2)}</span>
                            </div>
                        </div>
                    </div>
                </>
            ),
        },
        {
            key: 'attachments',
            label: t('screens:customerInvoiceDialog.tabs.attachments'),
            content: <AttachmentsPanel parentRecordId={data.id || ''} />,
        },
    ].filter(tab => !!data.id || tab.key === 'general');

    return (
        <>
            <ModalDialog
                open={open}
                title={isNew ? t('screens:customerInvoiceDialog.newInvoice') : t('screens:customerInvoiceDialog.editInvoice')}
                subtitle={
                    !isNew && data.reference
                        ? `${data.invoiceNumber ? `${data.invoiceNumber} · ` : ''}${data.reference}`
                        : undefined
                }
                onClose={onClose}
                onSubmit={handleSave}
                width={1050}
                minHeight="80vh"
                bodyVariant="tabs"
                footerLeft={!isNew ? (
                    <>
                        <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            disabled={data.isBooked}
                            title={data.isBooked ? t('screens:customerInvoiceDialog.actions.deleteDisabledBooked') : undefined}
                            onClick={() => setShowDeleteConfirm(true)}
                        >
                            <FaTrash style={{ marginRight: 5 }} />{t('screens:customerInvoiceDialog.actions.delete')}
                        </button>
                        {!data.isBooked && (
                            <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setShowBookConfirm(true)}>
                                <FaBook style={{ marginRight: 5 }} />{t('screens:customerInvoiceDialog.actions.book')}
                            </button>
                        )}
                        {!data.isPaid && data.isBooked && (
                            <button
                                type="button"
                                className="btn btn-outline-success btn-sm"
                                onClick={() => {
                                    setBookPaymentDate(new Date().toISOString().split('T')[0]);
                                    setShowBookPaymentConfirm(true);
                                }}
                            >
                                <FaMoneyBillWave style={{ marginRight: 5 }} />{t('screens:customerInvoiceDialog.actions.bookPayment')}
                            </button>
                        )}
                    </>
                ) : undefined}
                footerRight={
                    <>
                        <button type="button" className="btn btn-outline-secondary" onClick={() => setShowInvoiceView(true)}>
                            <FaEye style={{ marginRight: 5 }} />{t('screens:customerInvoiceDialog.actions.viewInvoice')}
                        </button>
                        <button type="submit" className="btn btn-primary">
                            <FaSave style={{ marginRight: 5 }} />{t('screens:customerInvoiceDialog.actions.save')}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            <FaTimes style={{ marginRight: 5 }} />{t('screens:customerInvoiceDialog.actions.close')}
                        </button>
                    </>
                }
            >
                <GenericVerticalTabs tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />
            </ModalDialog>
            <InvoiceView
                open={showInvoiceView}
                invoice={{ ...data, totalAmountExVat: totalExVat, totalAmountIncVat: totalIncVat }}
                customer={customers.find(c => c.id === data.customerId)}
                onClose={() => setShowInvoiceView(false)}
            />
            <PopupDialog
                open={showDeleteConfirm}
                title={t('screens:customerInvoiceDialog.deleteDialog.title')}
                onClose={() => setShowDeleteConfirm(false)}
                actions={
                    <>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowDeleteConfirm(false)}>
                            {t('common:cancel')}
                        </button>
                        <button type="button" className="btn btn-danger btn-sm" onClick={() => onDelete(data)}>
                            <FaTrash style={{ marginRight: 5 }} />{t('screens:customerInvoiceDialog.deleteDialog.confirm')}
                        </button>
                    </>
                }
            >
                <p>{t('screens:customerInvoiceDialog.deleteDialog.message')}</p>
            </PopupDialog>
            <PopupDialog
                open={showBookConfirm}
                title={t('screens:customerInvoiceDialog.bookDialog.title')}
                onClose={() => setShowBookConfirm(false)}
                actions={
                    <>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowBookConfirm(false)}>
                            {t('common:cancel')}
                        </button>
                        <button type="button" className="btn btn-primary btn-sm" onClick={async () => {
                            try { await onBook(data); setData(prev => ({ ...prev, isBooked: true })); } catch { }
                            setShowBookConfirm(false);
                        }}>
                            <FaBook style={{ marginRight: 5 }} />{t('screens:customerInvoiceDialog.bookDialog.confirm')}
                        </button>
                    </>
                }
            >
                <p>{t('screens:customerInvoiceDialog.bookDialog.message')}</p>
            </PopupDialog>
            <PopupDialog
                open={showBookPaymentConfirm}
                title={t('screens:customerInvoiceDialog.bookPaymentDialog.title')}
                onClose={() => setShowBookPaymentConfirm(false)}
                actions={
                    <>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowBookPaymentConfirm(false)}>
                            {t('common:cancel')}
                        </button>
                        <button
                            type="button"
                            className="btn btn-success btn-sm"
                            disabled={!bookPaymentDate}
                            onClick={async () => {
                                try { await onBookPayment(data, bookPaymentDate); setData(prev => ({ ...prev, isPaid: true })); } catch { }
                                setShowBookPaymentConfirm(false);
                            }}
                        >
                            <FaMoneyBillWave style={{ marginRight: 5 }} />{t('screens:customerInvoiceDialog.bookPaymentDialog.confirm')}
                        </button>
                    </>
                }
            >
                <p>{t('screens:customerInvoiceDialog.bookPaymentDialog.message')}</p>
                <div className="mb-3">
                    <label className="form-label">{t('screens:customerInvoiceDialog.bookPaymentDialog.paymentDate')}</label>
                    <input
                        type="date"
                        className="form-control"
                        value={bookPaymentDate}
                        onChange={e => setBookPaymentDate(e.target.value)}
                    />
                </div>
            </PopupDialog>
            {showImportTimewriting && (
                <ImportTimewritingDialog
                    invoiceId={data.id}
                    vatRates={vatRates}
                    products={products}
                    onImport={handleImportTimewriting}
                    onClose={() => setShowImportTimewriting(false)}
                />
            )}
        </>
    );
};

const ImportTimewritingDialog: React.FC<{
    invoiceId: string;
    vatRates: VatRate[];
    products: ProductOrService[];
    onImport: (item: CustomerInvoiceLineItem, projectId: string, from: string, to: string) => void;
    onClose: () => void;
}> = ({ invoiceId, vatRates, products, onImport, onClose }) => {
    const { t } = useTranslation(['common', 'screens']);
    const [projects, setProjects] = useState<Project[]>([]);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [projectId, setProjectId] = useState('');
    const [productId, setProductId] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const projectsProvider = useMemo(() => new ProjectsRestDataProvider(), []);
    const timeWritingProvider = useMemo(() => new TimeWritingRestDataProvider(), []);

    useEffect(() => {
        projectsProvider.getAll().then(setProjects).catch(() => setProjects([]));
    }, [projectsProvider]);

    const handleOk = async () => {
        if (!fromDate || !toDate || !projectId || !productId) {
            setError(t('screens:customerInvoiceDialog.errors.fillAllFields'));
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const entries = await timeWritingProvider.getByProject(projectId);
            const filtered = entries.filter(e => e.date >= fromDate && e.date <= toDate);
            const totalHours = Math.round(filtered.reduce((sum, e) => sum + Number(e.hours), 0) * 100) / 100;

            if (totalHours === 0) {
                setError(t('screens:customerInvoiceDialog.importDialog.noHoursFound'));
                return;
            }

            const product = products.find(p => p.id === productId)!;
            const vatRate = product.vat
                ? (vatRates.find(v => v.id === product.vat!.id) ?? defaultVatRate(vatRates))
                : defaultVatRate(vatRates);
            const project = projects.find(p => p.id === projectId);

            onImport(
                computeItem({
                    id: crypto.randomUUID(),
                    customerInvoiceId: invoiceId,
                    description: `${product.name} — ${project?.name ?? ''} (${fromDate} / ${toDate})`,
                    quantity: totalHours,
                    unitPriceExVat: product.unitPriceExVat ?? 0,
                    vatRate,
                    totalPriceExVat: 0,
                    totalPriceIncVat: 0,
                }),
                projectId,
                fromDate,
                toDate,
            );
        } catch (e: any) {
            setError(e.message ?? 'Failed to import timewriting.');
        } finally {
            setLoading(false);
        }
    };

    const projectOptions = [
        { label: t('screens:customerInvoiceDialog.importDialog.selectProject'), value: '' },
        ...projects.map(p => ({ label: p.name, value: p.id ?? '' })),
    ];
    const productOptions = [
        { label: t('screens:customerInvoiceDialog.importDialog.selectProduct'), value: '' },
        ...products.map(p => ({ label: p.articleCode ? `[${p.articleCode}] ${p.name}` : p.name, value: p.id })),
    ];

    return (
        <PopupDialog open title={t('screens:customerInvoiceDialog.importDialog.title')} onClose={onClose} actions={
            <>
                <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>{t('common:cancel')}</button>
                <button type="button" className="btn btn-primary btn-sm" onClick={handleOk} disabled={loading}>
                    {loading ? t('screens:customerInvoiceDialog.importDialog.importing') : t('screens:customerInvoiceDialog.importDialog.confirm')}
                </button>
            </>
        }>
            <div className="inv-import-tw">
                <div className="inv-import-tw__row">
                    <div className="inv-import-tw__col">
                        <label className="form-label">{t('screens:customerInvoiceDialog.importDialog.fromDate')}</label>
                        <input type="date" className="form-control form-control-sm" value={fromDate} onChange={e => setFromDate(e.target.value)} />
                    </div>
                    <div className="inv-import-tw__col">
                        <label className="form-label">{t('screens:customerInvoiceDialog.importDialog.toDate')}</label>
                        <input type="date" className="form-control form-control-sm" value={toDate} onChange={e => setToDate(e.target.value)} />
                    </div>
                </div>
                <SelectInput label={t('screens:customerInvoiceDialog.importDialog.project')} value={projectId} options={projectOptions} onChange={setProjectId} />
                <SelectInput label={t('screens:customerInvoiceDialog.importDialog.mapToProduct')} value={productId} options={productOptions} onChange={setProductId} />
                {error && <div className="inv-import-tw__error">{error}</div>}
            </div>
        </PopupDialog>
    );
};

export default CustomerInvoiceDialog;
